import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, waitlistApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import AdminBlogManagement from '../components/AdminBlogManagement';
import AdminEventManagement from '../components/AdminEventManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  
  // Default example data
  const defaultStats = {
    users: {
      total: 5,
      therapists: 2,
      clients: 3
    },
    therapists: {
      pendingVerifications: 0
    },
    bookings: {
      total: 3
    },
    revenue: {
      monthly: 12000,
      growth: 15
    },
    recent: {
      bookings: [
        { id: 1, clientName: 'John Doe', therapistName: 'Dr. Sarah', date: '2024-01-15', status: 'confirmed' },
        { id: 2, clientName: 'Jane Smith', therapistName: 'Dr. Mike', date: '2024-01-16', status: 'pending' },
        { id: 3, clientName: 'Bob Johnson', therapistName: 'Dr. Sarah', date: '2024-01-17', status: 'completed' }
      ],
      users: [
        { id: 1, name: 'Dr. Sarah', role: 'physiotherapist', email: 'sarah@example.com' },
        { id: 2, name: 'Dr. Mike', role: 'physiotherapist', email: 'mike@example.com' },
        { id: 3, name: 'John Doe', role: 'client', email: 'john@example.com' },
        { id: 4, name: 'Jane Smith', role: 'client', email: 'jane@example.com' },
        { id: 5, name: 'Bob Johnson', role: 'client', email: 'bob@example.com' }
      ]
    }
  };
  
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBookingModal, setSelectedBookingModal] = useState(null);

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await adminApi.updateBookingStatus(bookingId, { status: newStatus });
      setStats(prev => ({
        ...prev,
        recent: {
          ...prev.recent,
          bookings: prev.recent.bookings.map(b => ((b._id === bookingId || b.id === bookingId) ? { ...b, status: newStatus } : b))
        }
      }));
      if (selectedBookingModal && (selectedBookingModal._id === bookingId || selectedBookingModal.id === bookingId)) {
        setSelectedBookingModal(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Failed to update booking status:', err);
      alert(err.message || 'Failed to update status');
    }
  };

  useEffect(() => {
    if (!authSession || !authSession?.token || authSession?.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const [usersRes, bookingsRes, pendingRes, analyticsRes, waitlistRes] = await Promise.allSettled([
          adminApi.getUsers({ limit: 100 }),
          adminApi.getBookings({ limit: 100 }),
          adminApi.pendingVerifications(),
          adminApi.getOverviewAnalytics(),
          waitlistApi.adminGetWaitlist({ limit: 1 })
        ]);
        
        const usersData = usersRes.status === 'fulfilled' ? (usersRes.value?.users || usersRes.value?.data || []) : [];
        const bookingsData = bookingsRes.status === 'fulfilled' ? (bookingsRes.value?.bookings || bookingsRes.value?.data || []) : [];
        const pendingData = pendingRes.status === 'fulfilled' ? (pendingRes.value?.pending || pendingRes.value?.data || []) : [];
        const analyticsData = analyticsRes.status === 'fulfilled' ? (analyticsRes.value?.overview || analyticsRes.value?.data || {}) : {};
        const waitlistCount = waitlistRes.status === 'fulfilled' ? (waitlistRes.value?.total || waitlistRes.value?.stats?.total || 0) : 0;

        const therapistsCount = usersData.filter(u => u.role === 'physiotherapist').length;
        const clientsCount = usersData.filter(u => u.role === 'client').length;

        const mappedBookings = bookingsData.map(b => {
          const clientObj = b.client || (typeof b.clientId === 'object' ? b.clientId : {}) || {};
          const physioProfile = b.physiotherapistProfileId || {};
          const physioUser = (typeof physioProfile === 'object' && physioProfile.userId) ? physioProfile.userId : (b.therapist || {});

          const clientName = clientObj.name || b.clientName || 'Patient';
          const clientEmail = clientObj.email || b.clientEmail || 'N/A';
          const clientPhone = clientObj.phone || b.clientPhone || 'N/A';

          const therapistName = physioUser.name || physioProfile.name || b.therapistName || b.therapist?.name || 'Therapist';
          const therapistEmail = physioUser.email || physioProfile.email || b.therapistEmail || 'N/A';
          const therapistPhone = physioUser.phone || physioProfile.phone || b.therapistPhone || 'N/A';
          const therapistSpec = physioUser.specialization || physioProfile.specialization || b.specialization || 'Clinical Psychologist';

          const schedDateObj = b.scheduledAt ? new Date(b.scheduledAt) : null;
          const dateStr = schedDateObj ? schedDateObj.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : (b.date || 'TBD');
          const timeStr = schedDateObj ? schedDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (b.time || '10:00 AM');

          return {
            _id: b._id || b.id,
            id: b._id || b.id,
            clientName,
            clientEmail,
            clientPhone,
            therapistName,
            therapistEmail,
            therapistPhone,
            therapistSpec,
            date: dateStr,
            time: timeStr,
            scheduledAt: b.scheduledAt,
            mode: b.mode || b.sessionType || 'online',
            status: b.status || 'pending',
            amount: b.amount || b.price || 1500,
            paymentStatus: b.paymentStatus || 'paid',
            googleMeetLink: b.googleMeetLink || b.meetingLink || 'https://meet.google.com/new',
            offlineAddress: b.offlineAddress || b.location || 'Clinic Venue',
            notes: b.notes || 'Standard consultation session',
            createdAt: b.createdAt
          };
        });

        setStats({
          users: {
            total: usersData.length,
            therapists: therapistsCount,
            clients: clientsCount
          },
          therapists: {
            pendingVerifications: pendingData.length
          },
          bookings: {
            total: bookingsData.length
          },
          waitlist: {
            total: waitlistCount
          },
          revenue: {
            monthly: analyticsData.monthlyRevenue || 12500,
            growth: analyticsData.revenueGrowth || 12
          },
          recent: {
            bookings: mappedBookings,
            users: usersData.slice(0, 5).map(u => ({
              id: u._id || u.id,
              name: u.name || 'User',
              role: u.role || 'client',
              email: u.email || 'N/A'
            }))
          }
        });
      } catch (err) {
        console.error('Dashboard error:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [authSession?.token, navigate]);

  if (!authSession?.token || authSession?.user?.role !== 'admin') {
    return null;
  }

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <main className="admin-main-wrapper">
        {/* Page Header */}
        <section className="page-header" style={{
          backgroundImage: 'url(/assets/images/backgrounds/page-header-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 0',
          textAlign: 'center',
          color: 'white',
          marginBottom: '40px'
        }}>
          <div style={{
            background: 'rgba(0,0,0,0.4)',
            padding: '60px 0',
            textAlign: 'center'
          }}>
            <h2 style={{ fontSize: '32px', marginBottom: '20px', color: 'white' }}>Admin Dashboard</h2>
            <div style={{ fontSize: '14px' }}>
              <span><a href="/" style={{ color: 'white', textDecoration: 'none' }}>Home</a></span>
              <span style={{ margin: '0 10px' }}>/</span>
              <span>Admin Dashboard</span>
            </div>
          </div>
        </section>

        {/* Dashboard Content */}
        <section style={{ padding: '60px 0', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px' }}>
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ fontSize: '28px', color: '#333', marginBottom: '5px' }}>Welcome back, {authSession.user?.name}!</h2>
            <p style={{ color: '#666', margin: 0 }}>Here's what's happening with your platform today.</p>
          </div>

          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '40px'
          }}>
            {[
              { icon: '👥', title: stats?.users?.total || 0, label: 'Total Users', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', path: '/admin/users' },
              { icon: '🩺', title: stats?.users?.therapists || 0, label: 'Therapists', bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', path: '/admin/therapists' },
              { icon: '👤', title: stats?.users?.clients || 0, label: 'Clients', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', path: '/admin/users' },
              { icon: '⏳', title: stats?.waitlist?.total || 0, label: 'Waitlist Signups', bg: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)', path: '/admin/waitlist' },
              { icon: '✓', title: stats?.therapists?.pendingVerifications || 0, label: 'Pending Verifications', bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)', path: '/admin/verifications' },
              { icon: '📅', title: stats?.bookings?.total || 0, label: 'Total Bookings', bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', path: '/admin/bookings' },
              { icon: '💰', title: `₹${(stats?.revenue?.monthly || 0).toLocaleString()}`, label: 'Monthly Revenue', bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', path: '/admin/analytics' }
            ].map((card, idx) => (
              <div key={idx} 
                onClick={() => card.path && navigate(card.path)}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  cursor: card.path ? 'pointer' : 'default',
                  transition: 'transform 0.3s, box-shadow 0.3s'
                }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.1)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: card.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  {card.icon}
                </div>
                <div>
                  <h3 style={{ fontSize: '28px', margin: 0, color: '#333' }}>{card.title}</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{card.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '25px',
            marginBottom: '30px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>Quick Actions</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <a href="/admin/verifications" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                ✓ Review Verifications
                {stats?.therapists?.pendingVerifications > 0 && (
                  <span style={{
                    background: '#ef4444',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    marginLeft: 'auto'
                  }}>
                    {stats?.therapists?.pendingVerifications}
                  </span>
                )}
              </a>
              <a href="/admin/waitlist" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(168, 85, 247, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                ⏳ View Waitlist Signups
                {stats?.waitlist?.total > 0 && (
                  <span style={{
                    background: 'rgba(255,255,255,0.3)',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    marginLeft: 'auto'
                  }}>
                    {stats?.waitlist?.total}
                  </span>
                )}
              </a>
              <a href="/admin/users" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                👥 Manage Users
              </a>
              <a href="/admin/therapists" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                👨‍⚕️ Manage Practitioners
              </a>
              <a href="/admin/bookings" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                📅 Manage Bookings
              </a>
              <a href="/admin/content" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                📰 Review Content
              </a>
              <a href="/admin/analytics" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                📊 View Analytics
              </a>
              <a href="/admin/resources" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                📦 Manage Resources
              </a>
              <a href="/admin/founders-message" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }} onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(124, 75, 185, 0.4)';
              }} onMouseOut={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                💬 Founder's Message
              </a>
            </div>
          </div>

          {/* Recent Activity */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            {/* Recent Bookings */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '25px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a', fontWeight: 800 }}>📅 Recent Platform Bookings</h3>
                <button 
                  onClick={() => navigate('/admin/bookings')} 
                  style={{ border: 'none', background: '#eff6ff', color: '#2563eb', padding: '6px 14px', borderRadius: '20px', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                >
                  View All ({stats.bookings.total}) →
                </button>
              </div>

              <div>
                {stats?.recent?.bookings?.length > 0 ? (
                  stats.recent.bookings.slice(0, 6).map((booking, idx) => {
                    const badgeBg = booking.status === 'confirmed' ? '#dcfce7' : booking.status === 'completed' ? '#dbeafe' : booking.status === 'pending' ? '#fef3c7' : '#fee2e2';
                    const badgeColor = booking.status === 'confirmed' ? '#15803d' : booking.status === 'completed' ? '#1d4ed8' : booking.status === 'pending' ? '#b45309' : '#b91c1c';

                    return (
                      <div key={booking._id || idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 0',
                        borderBottom: idx < Math.min(stats.recent.bookings.length, 6) - 1 ? '1px solid #f1f5f9' : 'none',
                        gap: '12px'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14.5px' }}>
                              👤 {booking.clientName}
                            </span>
                            <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: booking.mode === 'online' ? '#eff6ff' : '#f0fdf4', color: booking.mode === 'online' ? '#2563eb' : '#166534', fontWeight: 700 }}>
                              {booking.mode === 'online' ? '💻 Online' : '🏥 Clinic'}
                            </span>
                          </div>

                          <div style={{ fontSize: '12.5px', color: '#475569' }}>
                            with 🩺 <strong>{booking.therapistName}</strong> ({booking.therapistSpec})
                          </div>

                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            📅 {booking.date} • 🕐 {booking.time}
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            background: badgeBg,
                            color: badgeColor,
                            textTransform: 'capitalize'
                          }}>
                            {booking.status}
                          </span>

                          <button
                            onClick={() => setSelectedBookingModal(booking)}
                            style={{
                              border: '1px solid #cbd5e1',
                              background: '#ffffff',
                              color: '#334155',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            👁️ Details
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p style={{ color: '#94a3b8', textAlign: 'center', padding: '30px', fontSize: '14px' }}>No bookings found on the platform.</p>
                )}
              </div>
            </div>

            {/* Recent Users */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '25px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ marginBottom: '20px', color: '#333' }}>Recent Users</h3>
              <div>
                {stats?.recent?.users?.length > 0 ? (
                  stats.recent.users.map((user, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '15px 0',
                      borderBottom: idx < stats.recent.users.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                        marginRight: '12px',
                        fontSize: '14px'
                      }}>
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 500, color: '#333', margin: 0 }}>{user.name || 'N/A'}</p>
                        <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>{user.email}</p>
                      </div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        background: user.role === 'admin' ? '#fce7f3' : user.role === 'physiotherapist' ? '#d1fae5' : '#dbeafe',
                        color: user.role === 'admin' ? '#be185d' : user.role === 'physiotherapist' ? '#059669' : '#2563eb'
                      }}>
                        {user.role === 'physiotherapist' ? 'therapist' : user.role}
                      </span>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No recent users</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* Blog Management Section */}
        <section style={{ padding: '40px 0', background: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px' }}>
            <AdminBlogManagement />
          </div>
        </section>

        {/* Event Management Section */}
        <section style={{ padding: '40px 0 60px 0', background: '#f8fafc' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px' }}>
            <AdminEventManagement />
          </div>
        </section>
      </main>

      <style>{`
        .admin-main-wrapper {
          margin-left: 270px;
          min-height: 100vh;
          transition: margin-left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          overflow-x: hidden;
          width: calc(100% - 270px);
        }

        @media (max-width: 1024px) {
          .admin-main-wrapper {
            margin-left: 0 !important;
            width: 100% !important;
            padding-top: 56px;
            overflow-x: hidden;
          }
          .admin-main-wrapper .page-header {
            padding: 30px 16px !important;
            margin-bottom: 20px !important;
          }
          .admin-main-wrapper section {
            padding: 24px 0 !important;
          }
        }

        .dashboard-loading, .dashboard-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          background: #f8f9fa;
        }
        .dashboard-error {
          text-align: center;
          padding: 60px;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* BOOKING DETAILS MODAL */}
      {selectedBookingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            width: '600px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
            padding: '28px',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px', background: '#eff6ff', color: '#2563eb', textTransform: 'uppercase' }}>
                  Booking #{selectedBookingModal._id?.slice(-6)}
                </span>
                <h3 style={{ margin: '6px 0 0 0', fontSize: '19px', color: '#0f172a', fontWeight: 800 }}>
                  Session Details & Info
                </h3>
              </div>
              <button
                onClick={() => setSelectedBookingModal(null)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13.5px', color: '#334155' }}>
              {/* Patient Details Card */}
              <div style={{ background: '#f8fafc', padding: '14px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px', fontSize: '14px' }}>👤 Patient Information</div>
                <div><strong>Name:</strong> {selectedBookingModal.clientName}</div>
                <div><strong>Email:</strong> {selectedBookingModal.clientEmail}</div>
                <div><strong>Phone:</strong> {selectedBookingModal.clientPhone}</div>
              </div>

              {/* Therapist Details Card */}
              <div style={{ background: '#f0fdf4', padding: '14px 18px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 700, color: '#166534', marginBottom: '6px', fontSize: '14px' }}>🩺 Therapist Information</div>
                <div><strong>Name:</strong> {selectedBookingModal.therapistName}</div>
                <div><strong>Specialization:</strong> {selectedBookingModal.therapistSpec}</div>
                <div><strong>Email:</strong> {selectedBookingModal.therapistEmail}</div>
                <div><strong>Phone:</strong> {selectedBookingModal.therapistPhone}</div>
              </div>

              {/* Session Schedule & Mode */}
              <div style={{ background: '#ffffff', padding: '14px 18px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px', fontSize: '14px' }}>📅 Session Schedule</div>
                <div><strong>Date:</strong> {selectedBookingModal.date}</div>
                <div><strong>Time:</strong> {selectedBookingModal.time}</div>
                <div><strong>Mode:</strong> {selectedBookingModal.mode === 'online' ? '💻 Online Video Session' : '🏥 Offline Clinic Consultation'}</div>
                {selectedBookingModal.mode === 'online' && selectedBookingModal.googleMeetLink && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>🎥 Google Meet Room:</strong>{' '}
                    <a href={selectedBookingModal.googleMeetLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 700 }}>
                      {selectedBookingModal.googleMeetLink}
                    </a>
                  </div>
                )}
                {selectedBookingModal.mode === 'offline' && selectedBookingModal.offlineAddress && (
                  <div style={{ marginTop: '4px' }}>
                    <strong>🏥 Clinic Venue:</strong> {selectedBookingModal.offlineAddress}
                  </div>
                )}
              </div>

              {/* Payment & Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div>
                  <div><strong>Fee Amount:</strong> ₹{selectedBookingModal.amount}</div>
                  <div><strong>Payment Status:</strong> <span style={{ color: '#16a34a', fontWeight: 700 }}>{selectedBookingModal.paymentStatus}</span></div>
                </div>
                <div>
                  <strong>Booking Status:</strong>{' '}
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background: selectedBookingModal.status === 'confirmed' ? '#dcfce7' : selectedBookingModal.status === 'completed' ? '#dbeafe' : selectedBookingModal.status === 'pending' ? '#fef3c7' : '#fee2e2',
                    color: selectedBookingModal.status === 'confirmed' ? '#15803d' : selectedBookingModal.status === 'completed' ? '#1d4ed8' : selectedBookingModal.status === 'pending' ? '#b45309' : '#b91c1c'
                  }}>
                    {selectedBookingModal.status}
                  </span>
                </div>
              </div>

              {/* Status Action Buttons */}
              <div style={{ marginTop: '10px' }}>
                <label style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', display: 'block', marginBottom: '8px' }}>Update Booking Status:</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['confirmed', 'completed', 'cancelled'].map(st => (
                    <button
                      key={st}
                      onClick={() => handleUpdateBookingStatus(selectedBookingModal._id || selectedBookingModal.id, st)}
                      disabled={selectedBookingModal.status === st}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '16px',
                        border: 'none',
                        fontWeight: 700,
                        fontSize: '12px',
                        cursor: selectedBookingModal.status === st ? 'default' : 'pointer',
                        opacity: selectedBookingModal.status === st ? 0.5 : 1,
                        background: st === 'confirmed' ? '#16a34a' : st === 'completed' ? '#2563eb' : '#dc2626',
                        color: '#ffffff'
                      }}
                    >
                      Mark as {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setSelectedBookingModal(null)}
                style={{ padding: '9px 20px', borderRadius: '20px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;
